import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  UserCircle,
  LogOut,
  UserPlus,
  Stethoscope,
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Inbox,
  FileText,
  BarChart3,
  X,  FileBadge,
  Settings as SettingsIcon,
} from "lucide-react";
import applogo from "../../assets/Images/applogo.png";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/slices";
import Avatar from "../ui/Avatar";

export default function Sidebar({
  isLg,
  mobileNavOpen,
  setMobileNavOpen,
  collapsed,
  setCollapsed,
  profileData,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const displayName = profileData?.displayName ?? "Doctor";
  const photoUrl = profileData?.photoUrl ?? null;
  const specialization = profileData?.specialization ?? profileData?.speciality ?? "";
  // Polyclinic white-labeling (CuoreMD spec) — branding only, see backend models/Clinic.js.
  // `clinicId` arrives populated from getDoctorProfile when the doctor belongs to one.
  const clinic = profileData?.clinicId && typeof profileData.clinicId === "object" ? profileData.clinicId : null;

  // CuoreCare and Walk-in EMR are separate, independently admin-granted capabilities
  // (isCuoreCareDoctor / emrEnabled — see models/Doctor.js on the backend). Walk-ins now live
  // INSIDE the CuoreCare console (Today/Upcoming/Completed cover both channels, since a walk-in
  // is an Appointment with mode "in_person" the same as a teleconsult), so there is no separate
  // "Walk-in EMR" nav entry any more, and "Requests" is no longer its own destination either —
  // a pending request surfaces under Today or Upcoming based on its date, with Accept/Decline
  // inline. Reception keeps its own dedicated Requests page (below), since a receptionist works
  // request-by-request across whatever's pending, not a dated queue.
  // A receptionist gets exactly one destination. Their token already fails every doctor
  // guard server-side, so this is presentation only — it stops them being shown links that
  // would 403; it is NOT the security boundary.
  const isReception = localStorage.getItem("role") === "reception";

  /* RECEPTION'S FOUR DESTINATIONS, matching the reviewed list exactly: "Day schedule; Add
     patient; Accept patients (today & upcoming); Casesheet."

     Requests is where today's and upcoming bookings are accepted. Add Patient registers the
     person at the desk. The case sheet is reached THROUGH the schedule — from the visit the
     patient is here for — rather than as its own sidebar entry, because a case sheet with no
     patient attached is a search box, not a destination.

     Every one is scoped server-side to the ONE doctor on the reception token
     (requireScheduleAccess / requireFrontDeskPatientAccess). From a visit the front desk fills
     the case-sheet intake, vitals & labs, vaccination and billing (items 12, 12.i, 20); the
     prescription and certificate stay doctor-only (requireVisitDocumentationAccess). */
  const navItems = isReception
    ? [
        { to: "/requests", icon: <Inbox size={20} />, label: "Requests" },
        { to: "/schedule", icon: <CalendarDays size={20} />, label: "Day Schedule" },
        { to: "/cuorecare/patients/new", icon: <UserPlus size={20} />, label: "Add Patient" },
        { to: "/cuorecare/availability", icon: <CalendarClock size={20} />, label: "Availability" },
        { to: "/settings", icon: <SettingsIcon size={20} />, label: "Settings" },
      ]
    : [
    ...(profileData?.isCuoreCareDoctor
      ? [{ to: "/cuorecare", icon: <Stethoscope size={20} />, label: "CuoreCare" }]
      : []),
    /* Diagnostics is NOT a sidebar destination any more. It lives inside the consultation as
       its own tab (Consultation.jsx renders <LabResults embeddedPatient=... />), where it is
       already scoped to the patient in front of the doctor. The sidebar copy was a second,
       patient-less door to the same panel that opened on a search box. The /diagnostics route
       stays mounted for bookmarks. */
    // Analytics (CuoreMD spec) covers BOTH channels (walk-in + tele-consult revenue), so it
    // follows either capability, same as Availability below.
    ...(profileData?.isCuoreCareDoctor || profileData?.emrEnabled
      ? [{ to: "/analytics", icon: <BarChart3 size={20} />, label: "Analytics" }]
      : []),
    ...(profileData?.isCuoreCareDoctor || profileData?.emrEnabled
      ? [{ to: "/schedule", icon: <CalendarDays size={20} />, label: "Day Schedule" }]
      : []),
    /* CuoreMD Professional is built and routed, but HIDDEN until launch — the network is only
       worth opening once enough doctors are enrolled for it to have anyone in it. Restore this
       one line to bring it back; /professional stays mounted in the meantime. */
    /* /doctor-home, not /home: /home now lands a CuoreCare doctor on their day (see
       AppRoutes.jsx). This link is the overview itself, so it points straight at it. */
    { to: "/doctor-home", icon: <LayoutDashboard size={20} />, label: "Patients Health Overview" },
    {
      to: "/editprofile",
      icon: <UserCircle size={20} />,
      label: "Profile",
      children: [
        { to: "/editprofile#letterhead", icon: <FileText size={16} />, label: "Clinic Letterhead" },
        // §9/§10 — always reachable, including (especially) to an unverified doctor: it is the
        // one screen that unblocks every other clinical feature, so it stays nested but visible
        // rather than behind a collapsed accordion.
        { to: "/verification", icon: <FileBadge size={16} />, label: "Registration & Fees" },
        // Availability configures BOTH channels on one shared weekly template (see
        // pages/Doctor/CuoreCare/Availability.jsx), so it's shown whenever EITHER capability is
        // present — a walk-in-only doctor needs a way to reach it too, not just a teleconsult one.
        ...(profileData?.isCuoreCareDoctor || profileData?.emrEnabled
          ? [{ to: "/cuorecare/availability", icon: <CalendarClock size={16} />, label: "Availability" }]
          : []),
      ],
    },
    { to: "/addpatient", icon: <UserPlus size={20} />, label: "Invite Patients to Cuore" },
    // Appearance only — theme and text size. Deliberately the last entry: it is the one
    // destination a doctor visits once and then forgets about.
    { to: "/settings", icon: <SettingsIcon size={20} />, label: "Settings" },
  ];

  // Reception has no clinical profile to show in the sidebar header.
  const headerName = isReception ? displayName : `Dr. ${displayName}`;

  const showExpanded = !isLg || !collapsed;
  const drawerClosedMobile = !isLg && !mobileNavOpen;

  return (
    <motion.aside
      initial={false}
      animate={isLg ? { width: collapsed ? "4.5rem" : "15rem" } : false}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`fixed left-0 top-0 h-screen z-50 flex flex-col bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 shadow-lg lg:shadow-sm overflow-hidden transition-transform duration-300 ease-in-out
        w-[min(17.5rem,88vw)] shrink-0
        lg:w-auto lg:max-w-none
        ${drawerClosedMobile ? "-translate-x-full pointer-events-none" : "translate-x-0"}
        lg:translate-x-0 lg:pointer-events-auto
      `}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-3 sm:px-4 border-b border-slate-100 dark:border-slate-800 shrink-0 gap-2">
        <motion.div
          className="flex items-center overflow-hidden min-w-0 flex-1"
          animate={{ opacity: 1 }}
        >
          <AnimatePresence mode="wait">
            {isLg && collapsed ? (
              <motion.div
                key="icon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="w-8 h-8 rounded-lg bg-white border border-slate-100 dark:border-slate-700 flex items-center justify-center shrink-0 overflow-hidden"
              >
                <img src={clinic?.logoUrl || applogo} alt={clinic?.name || "CuoreMD"} className="w-full h-full object-contain p-0.5" />
              </motion.div>
            ) : clinic ? (
              // A clinic's identity replaces the CuoreMD wordmark entirely once a doctor
              // joins one — logo if they uploaded one, else just the name as text.
              <motion.div
                key="clinic-full"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2 min-w-0"
              >
                {clinic.logoUrl && (
                  <img src={clinic.logoUrl} alt={clinic.name} className="h-7 w-7 rounded-md object-contain shrink-0" />
                )}
                <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">{clinic.name}</span>
              </motion.div>
            ) : (
              <motion.img
                key="full"
                src={applogo}
                alt="CuoreMD"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                className="h-7 w-auto max-w-[9rem] sm:max-w-none object-left object-contain"
              />
            )}
          </AnimatePresence>
        </motion.div>

        <div className="flex items-center shrink-0 gap-0.5">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:inline-flex p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Doctor info */}
      <AnimatePresence>
        {showExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="px-4 py-4 border-b border-slate-100 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <Avatar src={photoUrl} name={displayName} size="md" />
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                  {headerName}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{isReception ? "Reception" : specialization || "Physician"}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav items */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        <div className="px-2 space-y-0.5">
          {navItems.map((item) => (
            <React.Fragment key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/editprofile"}
                onClick={() => {
                  if (!isLg) setMobileNavOpen(false);
                }}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 h-10 px-3 rounded-xl text-sm font-medium transition-all duration-150 group
                  ${isActive
                    ? "bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`shrink-0 ${isActive ? "text-brand-600 dark:text-brand-400" : ""}`}>
                      {item.icon}
                    </span>
                    <AnimatePresence>
                      {showExpanded && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="whitespace-nowrap flex-1 min-w-0 truncate"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </NavLink>

              {/* Nested children (Profile > Clinic Letterhead / Registration & Fees / Availability)
                  — always shown, never collapsed behind an accordion, so it stays
                  reachable in one glance for an unverified doctor. Hidden entirely when the
                  rail is collapsed to icon-only, same as the parent's label. */}
              {item.children && showExpanded && (
                <div className="ml-4 pl-3 border-l border-slate-100 dark:border-slate-800 space-y-0.5 mb-1">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      onClick={() => {
                        if (!isLg) setMobileNavOpen(false);
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 h-8 px-3 rounded-lg text-[13px] font-medium transition-all duration-150
                        ${isActive
                          ? "bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300"
                          : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                        }`
                      }
                    >
                      <span className="shrink-0">{child.icon}</span>
                      <span className="whitespace-nowrap flex-1 min-w-0 truncate">{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </nav>

      {/* Logout */}
      <div className="px-2 py-4 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 h-10 px-3 w-full rounded-xl text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-150"
        >
          <LogOut size={20} className="shrink-0" />
          <AnimatePresence>
            {showExpanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="whitespace-nowrap"
              >
                Sign Out
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
}
